import { useRef, useEffect, useCallback, useContext } from 'react';
import * as NormativeApi from '../../../../../../../../services/api/normative';
import { getDeliveryNote } from '../../../../../../../../services/redux/deliveryNotes/actions';
import { useOutletContext } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { NotificationsContext } from '../../../../../../../../context/NotificationsContext';

const useNormativeApi = () => {
  const pollingRef = useRef(null);
  const intl = useIntl();
  const dispatch = useDispatch();
  const { project } = useOutletContext();
  const { pushNotification } = useContext(NotificationsContext);

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, []);

  const getUpdate = useCallback(async (taskUpdateID) => {
    return new Promise((resolve, reject) => {
      let taskUpdate;

      const poll = async () => {
        try {
          const res = await NormativeApi.getUpdateTasks(taskUpdateID);
          taskUpdate = res.data;

          if (
            taskUpdate.state === 'PROGRESS' ||
            taskUpdate.state === 'PENDING'
          ) {
            pollingRef.current = setTimeout(poll, 1000);
          } else if (taskUpdate.state === 'FAILURE') {
            reject();
          } else if (taskUpdate.state === 'SUCCESS') {
            resolve();
          }
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  }, []);

  const updateDataScript = useCallback(
    async (isLotification, deliveryNoteId, idFinal, idInicial) => {
      const res = await NormativeApi.postScript({
        projectId: project.id_project,
        lotification: isLotification,
        deliveryNoteId,
        finalBatchId: idFinal,
        initialBatchId: idInicial,
      });

      return getUpdate(res.data);
    },
    [getUpdate, project.id_project],
  );

  const getProjectStatus = useCallback(
    async ({ deliveryNote, formValues }, isLotification) => {
      return new Promise(async (resolve, reject) => {
        try {
          const response = await NormativeApi.getProjectStatus(
            project.id_project,
          );

          if (response !== null) {
            if (!pollingRef.current?.hadScript) {
              pushNotification({
                message: intl.formatMessage({
                  id: 'pages.quality.normative.traceability.projectStatus.notification.info',
                  defaultMessage: 'Hay un script en curso.',
                }),
                type: 'info',
              });
            }
            pollingRef.current = { ...pollingRef.current, hadScript: true };

            pollingRef.current.timeout = setTimeout(() => {
              getProjectStatus({ deliveryNote, formValues }, isLotification)
                .then(resolve)
                .catch(reject);
            }, 1000);
          } else {
            if (pollingRef.current?.hadScript) {
              pollingRef.current = { ...pollingRef.current, hadScript: false };
              pushNotification({
                message: intl.formatMessage({
                  id: 'pages.quality.normative.traceability.projectStatus.notification.warning.finished',
                }),
                type: 'success',
              });
              dispatch(getDeliveryNote(deliveryNote.id));
              resolve();
            } else if (deliveryNote === undefined) {
              resolve();
            } else {
              pushNotification({
                message: intl.formatMessage({
                  id: 'pages.quality.normative.traceability.projectStatus.notification.success',
                }),
                type: 'success',
              });
              resolve();
            }
          }
        } catch (error) {
          pushNotification({
            message: intl.formatMessage({
              id: 'pages.quality.normative.traceability.projectStatus.notification.error',
            }),
            type: 'error',
          });
          reject(error);
        }
      });
    },
    [project.id_project, pushNotification, intl, dispatch],
  );

  return {
    getUpdate,
    updateDataScript,
    getProjectStatus,
  };
};

export default useNormativeApi;
